import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import { Eye, MousePointerClick, ShoppingBag, Percent } from 'lucide-react';
import eventAnalyticsApi from 'api/requests/eventAnalytics';
import MiniStatCard from 'modules/dashboard/statistics/components/MiniStatCard';
import { MiniCardSkeleton } from 'modules/dashboard/statistics/components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

const PAYMENT_METHOD_COLORS = ['#2BC866', '#690ce4', '#4F86F7'];
const PAYMENT_METHOD_LABELS = {
    pix: 'Pix',
    credit_card: 'Cartão',
    free: 'Gratuito',
};

export default function EventAccessCard({ eventId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        if (!eventId) {
            return;
        }

        let isMounted = true;
        setLoading(true);
        setErrored(false);

        eventAnalyticsApi.getAnalytics(eventId)
            .then(({ data }) => { if (isMounted) setData(data); })
            .catch(() => { if (isMounted) setErrored(true); })
            .finally(() => { if (isMounted) setLoading(false); });

        return () => { isMounted = false; };
    }, [eventId]);

    if (loading) {
        return (
            <Wrap>
                <KpiGrid>
                    <MiniCardSkeleton /><MiniCardSkeleton /><MiniCardSkeleton /><MiniCardSkeleton />
                </KpiGrid>
            </Wrap>
        );
    }

    // Seção de bônus, não crítica pro resto da tela: some silenciosamente em
    // vez de mostrar um erro se o backend falhar ou o evento ainda não tiver
    // nenhum acesso registrado.
    if (errored || !data) {
        return null;
    }

    const { views, checkout_clicks: checkoutClicks, orders_approved: ordersApproved, conversion, payment_methods: paymentMethods } = data;

    const paymentEntries = Object.keys(PAYMENT_METHOD_LABELS)
        .map((method, index) => ({
            method,
            label: PAYMENT_METHOD_LABELS[method],
            color: PAYMENT_METHOD_COLORS[index],
            count: paymentMethods?.[method]?.count || 0,
            percentage: paymentMethods?.[method]?.percentage || 0,
        }));

    const hasPayments = paymentEntries.some(entry => entry.count > 0);

    const donutData = {
        labels: paymentEntries.map(entry => entry.label),
        datasets: [{
            data: paymentEntries.map(entry => entry.count),
            backgroundColor: paymentEntries.map(entry => entry.color),
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: '#fff',
        }],
    };

    const donutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: (tooltipItem, chartData) => {
                    const label = chartData.labels[tooltipItem.index];
                    const value = chartData.datasets[0].data[tooltipItem.index];
                    return ` ${label}: ${value.toLocaleString('pt-BR')}`;
                },
            },
        },
    };

    return (
        <Wrap>
            <KpiGrid>
                <MiniStatCard
                    label="Visualizações"
                    value={(views?.total || 0).toLocaleString('pt-BR')}
                    icon={Eye}
                />
                <MiniStatCard
                    label="Cliques em comprar"
                    value={(checkoutClicks?.total || 0).toLocaleString('pt-BR')}
                    icon={MousePointerClick}
                />
                <MiniStatCard
                    label="Vendas aprovadas"
                    value={(ordersApproved || 0).toLocaleString('pt-BR')}
                    icon={ShoppingBag}
                />
                <MiniStatCard
                    label="Taxa de conversão"
                    value={`${((conversion?.view_to_purchase || 0) * 100).toFixed(1)}%`}
                    icon={Percent}
                />
            </KpiGrid>

            {hasPayments && (
                <SplitRow>
                    <DoughnutWrap>
                        <Doughnut data={donutData} options={donutOptions} />
                    </DoughnutWrap>
                    <SplitList>
                        {paymentEntries.map(entry => (
                            <SplitItem key={entry.method}>
                                <Dot color={entry.color} />
                                {entry.label}
                                <strong>{entry.count.toLocaleString('pt-BR')}</strong>
                            </SplitItem>
                        ))}
                    </SplitList>
                </SplitRow>
            )}
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const SplitRow = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
`;

const DoughnutWrap = styled.div`
    width: 140px;
    height: 140px;
    flex-shrink: 0;
`;

const SplitList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 160px;
`;

const SplitItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${colors.nightDark};

    strong {
        margin-left: auto;
    }
`;

const Dot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;
